import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { Avatar, SeloParabenizado, CartaoAniversarioModal } from "./EndomarketingPecas";
import Aniversariantes from "./EndomarketingAniversariantes";
import Comunicado from "./EndomarketingComunicado";
import Fotos from "./EndomarketingFotos";
import Eventos from "./EndomarketingEventos";
import Programas from "./EndomarketingProgramas";
import {
  type Colaborador,
  MESES,
  abrirWhatsApp,
  fmt,
  primeiroNome,
} from "../endomarketing-utils";
import {
  IconClose,
  IconStar,
  IconChat,
  IconUsers,
  IconFolder,
  IconRefresh,
  IconAward,
  IconSparkles,
} from "../icons";
import { compararNomes, ordenarPorNome } from "../ordem";

/**
 * ENDOMARKETING — a comunicação interna do RH.
 *
 * A tela abre no PAINEL: quem faz aniversário hoje e nos próximos dias, o que
 * está faltando no cadastro para a comunicação funcionar (foto, nascimento,
 * telefone) e o que foi comunicado por último. É a pergunta que o RH faz ao
 * abrir o sistema — "tem alguém para eu falar hoje?" — respondida de cara.
 *
 * As outras quatro telas são o trabalho em si:
 *   • Aniversariantes — do mês, com mensagem, cartão e controle de quem já foi
 *     parabenizado;
 *   • Comunicado — disparo em massa pelo WhatsApp, com modelos e histórico;
 *   • Fotos — o mural: foto de perfil e data de nascimento de cada um;
 *   • Eventos — as pastas de imagens do ano.
 *
 * Tudo em cima do cadastro que já existe: nenhuma lista de pessoas é digitada
 * aqui de novo.
 */

type Secao = "painel" | "aniversariantes" | "comunicado" | "fotos" | "eventos" | "programas";

const ABAS: { key: Secao; label: string; Icon: typeof IconStar }[] = [
  { key: "painel", label: "Painel", Icon: IconSparkles },
  { key: "aniversariantes", label: "Aniversariantes", Icon: IconStar },
  { key: "comunicado", label: "Comunicado", Icon: IconChat },
  { key: "fotos", label: "Fotos da equipe", Icon: IconUsers },
  { key: "eventos", label: "Eventos do ano", Icon: IconFolder },
  // Os dois programas internos (Realize um Sonho / Ideias que Transformam):
  // é aqui que as respostas do link voltam, por pessoa.
  { key: "programas", label: "Programas", Icon: IconAward },
];

export default function EndomarketingView() {
  const [secao, setSecao] = useState<Secao>("painel");
  const [aviso, setAviso] = useState<string | null>(null);

  // O aviso some sozinho: é confirmação de ação, não erro que precise de
  // decisão (erro de verdade aparece no formulário da própria seção).
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 6000);
    return () => clearTimeout(t);
  }, [aviso]);

  return (
    <div className="crud endo">
      <nav className="endo-abas">
        {ABAS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={secao === key ? "ativa" : ""}
            onClick={() => setSecao(key)}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </nav>

      {aviso && (
        <div className="endo-aviso">
          <span>{aviso}</span>
          <button className="icon-btn" title="Fechar" onClick={() => setAviso(null)}>
            <IconClose size={16} />
          </button>
        </div>
      )}

      {secao === "painel" && <Painel onAviso={setAviso} onIr={setSecao} />}
      {secao === "aniversariantes" && <Aniversariantes onAviso={setAviso} />}
      {secao === "comunicado" && <Comunicado onAviso={setAviso} />}
      {secao === "fotos" && <Fotos onAviso={setAviso} />}
      {secao === "eventos" && <Eventos onAviso={setAviso} />}
      {secao === "programas" && <Programas onAviso={setAviso} />}
    </div>
  );
}

/* ==================== Painel ==================== */

function Painel({
  onAviso,
  onIr,
}: {
  onAviso: (m: string) => void;
  onIr: (s: Secao) => void;
}) {
  const [dados, setDados] = useState<any>(null);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [cartao, setCartao] = useState<Colaborador | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setDados(await api.endomarketing.painel());
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Recarrega SEM passar pelo "Carregando…": com o post aberto, o retorno
   * antecipado abaixo desmontaria o modal no meio do envio (e a pessoa perderia
   * o fundo e a foto que tinha escolhido).
   */
  const atualizarSemPiscar = useCallback(async () => {
    try {
      setDados(await api.endomarketing.painel());
    } catch {
      /* a próxima carga normal mostra o erro */
    }
  }, []);

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;
  if (loading || !dados) return <div className="empty">Carregando…</div>;

  // Hoje é o mesmo dia para todos: ordem alfabética. A semana segue quem
  // chega primeiro (dias que faltam) e o nome só desempata.
  const hoje: Colaborador[] = ordenarPorNome((dados.hoje ?? []) as Colaborador[], (p) => p.nome);
  const semana: Colaborador[] = [...((dados.semana ?? []) as Colaborador[])].sort(
    (a, b) => (Number(a.faltam) || 0) - (Number(b.faltam) || 0) || compararNomes(a.nome, b.nome),
  );
  const c = dados.contagens ?? {};
  const mesAtual = MESES[new Date().getMonth()];

  function mensagemAniversario(p: Colaborador) {
    return (
      `Parabéns, ${primeiroNome(p.nome)}! 🎉\n\n` +
      "A equipe SERCAMP deseja um feliz aniversário e um ano muito especial. " +
      "Obrigado por fazer parte do nosso time!"
    );
  }

  async function parabenizar(p: Colaborador) {
    if (!abrirWhatsApp(p.telefone, mensagemAniversario(p))) {
      onAviso(`${p.nome} está sem telefone no cadastro — preencha em "Fotos da equipe".`);
      return;
    }
    await api.endomarketing.parabens(p.id, "nascimento").catch(() => {});
    await carregar();
  }

  async function desmarcar(p: Colaborador) {
    await api.endomarketing.parabens(p.id, "nascimento", true).catch(() => {});
    await carregar();
  }

  return (
    <div className="endo-painel">
      <div className="endo-indicadores">
        <Indicador
          rotulo="Aniversários hoje"
          valor={hoje.length}
          detalhe={hoje.length ? hoje.map((p) => primeiroNome(p.nome)).join(", ") : "ninguém hoje"}
          destaque={hoje.length > 0}
          onClick={() => onIr("aniversariantes")}
          Icon={IconStar}
        />
        <Indicador
          rotulo="Próximos 7 dias"
          valor={semana.length}
          detalhe={semana.length ? `o mais próximo em ${semana[0]!.faltam} dia(s)` : "nada na semana"}
          onClick={() => onIr("aniversariantes")}
          Icon={IconStar}
        />
        <Indicador
          rotulo={`Aniversariantes de ${mesAtual}`}
          valor={c.aniversarios_mes ?? 0}
          detalhe={`${c.empresa_mes ?? 0} de empresa no mês`}
          onClick={() => onIr("aniversariantes")}
          Icon={IconAward}
        />
        <Indicador
          rotulo="Comunicados no mês"
          valor={dados.comunicados?.no_mes ?? 0}
          detalhe={`${dados.comunicados?.total ?? 0} no total`}
          onClick={() => onIr("comunicado")}
          Icon={IconChat}
        />
        <Indicador
          rotulo="Eventos do ano"
          valor={dados.eventos?.no_ano ?? 0}
          detalhe={`${dados.eventos?.fotos ?? 0} foto(s) no acervo`}
          onClick={() => onIr("eventos")}
          Icon={IconFolder}
        />
        <Indicador
          rotulo="Equipe ativa"
          valor={c.ativos ?? 0}
          detalhe={`${c.ativos - (c.sem_foto ?? 0)} com foto`}
          onClick={() => onIr("fotos")}
          Icon={IconUsers}
        />
      </div>

      <div className="endo-painel-grade">
        <section className="endo-bloco">
          <header className="endo-bloco-head">
            <h3>
              <IconStar size={16} /> Aniversariantes de hoje
            </h3>
            <button className="btn-secondary btn-sm" onClick={carregar} title="Atualizar">
              <IconRefresh size={15} />
            </button>
          </header>

          {hoje.length === 0 ? (
            <p className="endo-vazio">
              Ninguém faz aniversário hoje. Os próximos aparecem no bloco ao lado.
            </p>
          ) : (
            <div className="endo-cards">
              {hoje.map((p) => (
                <article key={p.id} className="endo-card hoje">
                  <Avatar pessoa={p} tamanho="m" />
                  <div className="endo-card-info">
                    <strong>{p.nome}</strong>
                    <small className="muted">{p.cargo || p.departamento || "—"}</small>
                    <span className="endo-dia">
                      {p.fazendo ? `fazendo ${p.fazendo} anos` : "aniversário hoje"} 🎉
                    </span>
                  </div>
                  <div className="endo-card-acoes">
                    {p.parabenizado ? (
                      <SeloParabenizado onDesfazer={() => desmarcar(p)} />
                    ) : (
                      <button className="btn-whats btn-sm" onClick={() => parabenizar(p)}>
                        Parabenizar
                      </button>
                    )}
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => setCartao(p)}
                      title="Montar o post (cartão ou imagem pronta) e enviar pelo WhatsApp"
                    >
                      Cartão
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="endo-bloco">
          <header className="endo-bloco-head">
            <h3>
              <IconAward size={16} /> Próximos 7 dias
            </h3>
          </header>
          {semana.length === 0 ? (
            <p className="endo-vazio">Nenhum aniversário nos próximos dias.</p>
          ) : (
            <ul className="endo-proximos">
              {semana.map((p) => (
                <li key={p.id}>
                  <Avatar pessoa={p} tamanho="s" />
                  <div>
                    <strong>{p.nome}</strong>
                    <small className="muted">{p.cargo || p.departamento || "—"}</small>
                  </div>
                  <span className="badge">
                    {p.faltam === 1 ? "amanhã" : `em ${p.faltam} dias`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="endo-bloco">
          <header className="endo-bloco-head">
            <h3>Cadastro — o que está faltando</h3>
          </header>
          <p className="endo-vazio">
            Sem estes dados a comunicação não roda: sem telefone não há WhatsApp, sem
            nascimento a pessoa não aparece nos aniversários.
          </p>
          <div className="endo-pendencias">
            <Pendencia
              rotulo="sem telefone"
              qtd={c.sem_telefone ?? 0}
              total={c.ativos ?? 0}
              acao="Ver no cadastro"
              onClick={() =>
                onAviso("Preencha o telefone na aba Colaboradores — é o número do WhatsApp.")
              }
            />
            <Pendencia
              rotulo="sem data de nascimento"
              qtd={c.sem_nascimento ?? 0}
              total={c.ativos ?? 0}
              acao="Preencher"
              onClick={() => onIr("fotos")}
            />
            <Pendencia
              rotulo="sem foto"
              qtd={c.sem_foto ?? 0}
              total={c.ativos ?? 0}
              acao="Enviar fotos"
              onClick={() => onIr("fotos")}
            />
          </div>
        </section>

        <section className="endo-bloco">
          <header className="endo-bloco-head">
            <h3>
              <IconChat size={16} /> Últimos comunicados
            </h3>
            <button className="btn-secondary btn-sm" onClick={() => onIr("comunicado")}>
              Novo comunicado
            </button>
          </header>
          {(dados.comunicados?.ultimos ?? []).length === 0 ? (
            <p className="endo-vazio">Nenhum comunicado ainda.</p>
          ) : (
            <ul className="endo-ultimos">
              {dados.comunicados.ultimos.map((h: any) => (
                <li key={h.id}>
                  <strong>{h.titulo}</strong>
                  <small className="muted">
                    {/* Envio para grupo não tem lista de pessoas (enviados = 0). */}
                    {h.publico || "Todos"}
                    {h.enviados ? ` · ${h.enviados} pessoa(s)` : ""} ·{" "}
                    {new Date(h.criado_em).toLocaleDateString("pt-BR")}
                  </small>
                </li>
              ))}
            </ul>
          )}
        </section>

        {dados.eventos?.ultimo && (
          <section className="endo-bloco endo-bloco-largo">
            <header className="endo-bloco-head">
              <h3>
                <IconFolder size={16} /> Último evento
              </h3>
              <button className="btn-secondary btn-sm" onClick={() => onIr("eventos")}>
                Ver todos
              </button>
            </header>
            <div className="endo-ultimo-evento" onClick={() => onIr("eventos")}>
              <div className="endo-evento-capa">
                {dados.eventos.ultimo.capa ? (
                  <img
                    src={`/api/endomarketing/eventos/${dados.eventos.ultimo.id}/fotos/${encodeURIComponent(dados.eventos.ultimo.capa)}`}
                    alt={dados.eventos.ultimo.nome}
                  />
                ) : (
                  <span>sem fotos</span>
                )}
              </div>
              <div>
                <strong>{dados.eventos.ultimo.nome}</strong>
                <small className="muted">
                  {fmt(dados.eventos.ultimo.data)}
                  {dados.eventos.ultimo.local ? ` · ${dados.eventos.ultimo.local}` : ""}
                </small>
                <span className="badge">{dados.eventos.ultimo.fotos} foto(s)</span>
              </div>
            </div>
          </section>
        )}
      </div>

      {cartao && (
        <CartaoAniversarioModal
          pessoa={cartao}
          legenda={cartao.fazendo ? `${cartao.fazendo} anos` : "Parabéns!"}
          mensagem={mensagemAniversario(cartao)}
          onEnviado={async () => {
            // O painel só lista aniversário de NASCIMENTO (hoje e na semana).
            await api.endomarketing.parabens(cartao.id, "nascimento").catch(() => {});
            await atualizarSemPiscar();
          }}
          onClose={() => setCartao(null)}
        />
      )}
    </div>
  );
}

function Indicador({
  rotulo,
  valor,
  detalhe,
  destaque,
  onClick,
  Icon,
}: {
  rotulo: string;
  valor: number;
  detalhe: string;
  destaque?: boolean;
  onClick: () => void;
  Icon: typeof IconStar;
}) {
  return (
    <button type="button" className={`endo-ind ${destaque ? "destaque" : ""}`} onClick={onClick}>
      <span className="endo-ind-icone">
        <Icon size={17} />
      </span>
      <span className="endo-ind-valor">{valor}</span>
      <span className="endo-ind-rotulo">{rotulo}</span>
      <span className="endo-ind-detalhe">{detalhe}</span>
    </button>
  );
}

function Pendencia({
  rotulo,
  qtd,
  total,
  acao,
  onClick,
}: {
  rotulo: string;
  qtd: number;
  total: number;
  acao: string;
  onClick: () => void;
}) {
  const pct = total > 0 ? Math.round(((total - qtd) / total) * 100) : 100;
  return (
    <div className={`endo-pend ${qtd === 0 ? "ok" : ""}`}>
      <div className="endo-pend-topo">
        <span>
          <strong>{qtd}</strong> {rotulo}
        </span>
        {qtd > 0 && (
          <button className="btn-secondary btn-sm" onClick={onClick}>
            {acao}
          </button>
        )}
      </div>
      <div className="endo-barra">
        <div className="endo-barra-fill" style={{ width: `${pct}%` }} />
      </div>
      <small className="muted">{pct}% do cadastro completo</small>
    </div>
  );
}
